<script setup lang="ts">
import { ref } from 'vue';
import type { LoginParams } from '@/types/auth';
import MobileLogin from './MobileLogin.vue';
import WechatLogin from './WechatLogin.vue';
import AdminPasswordLogin from './AdminPasswordLogin.vue';

const emit = defineEmits<{
  submit: [params: LoginParams];
  error: [message: string];
}>();

const activeTab = ref('mobile');
const adminRef = ref<InstanceType<typeof AdminPasswordLogin> | null>(null);

// 微信登录/绑定总开关：本版本默认关闭，下版本通过 VITE_ENABLE_WECHAT_LOGIN=true 恢复
const enableWechatLogin = import.meta.env.VITE_ENABLE_WECHAT_LOGIN === 'true';

function handleMobileSubmit(payload: { mobile: string; code: string }) {
  emit('submit', { method: 'mobile_code', mobile: payload.mobile, code: payload.code });
}

function handleWechatSubmit(payload: { authCode: string }) {
  const method = window.innerWidth >= 768 ? 'wechat_qr' : 'wechat_mobile';
  emit('submit', { method, authCode: payload.authCode });
}

function handleAdminSubmit(payload: { username: string; password: string }) {
  emit('submit', { method: 'password', username: payload.username, password: payload.password });
}

function handleError(message: string) {
  emit('error', message);
}

function recordAdminFailure() {
  adminRef.value?.recordFailure();
}

defineExpose({ recordAdminFailure });
</script>

<template>
  <div class="w-full">
    <ElTabs v-model="activeTab" class="login-tabs" stretch>
      <ElTabPane label="手机号登录" name="mobile">
        <MobileLogin @submit="handleMobileSubmit" @error="handleError" />
      </ElTabPane>
      <ElTabPane v-if="enableWechatLogin" label="微信登录" name="wechat">
        <WechatLogin v-if="activeTab === 'wechat'" @submit="handleWechatSubmit" />
      </ElTabPane>
      <ElTabPane label="管理员登录" name="admin">
        <AdminPasswordLogin ref="adminRef" @submit="handleAdminSubmit" />
      </ElTabPane>
    </ElTabs>
  </div>
</template>

<style scoped>
.login-tabs :deep(.el-tabs__header) {
  margin-bottom: 24px;
}
.login-tabs :deep(.el-tabs__nav-wrap)::after {
  background-color: var(--color-divider) !important;
  height: 1px !important;
}
.login-tabs :deep(.el-tabs__item) {
  font-size: 14px !important;
  font-weight: 500 !important;
  color: var(--color-caption) !important;
  padding: 0 12px !important;
  height: 40px !important;
  line-height: 40px !important;
  transition: color 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
}
.login-tabs :deep(.el-tabs__item.is-active) {
  color: var(--color-primary) !important;
  font-weight: 600 !important;
}
.login-tabs :deep(.el-tabs__item:hover) {
  color: var(--color-primary-hover) !important;
}
.login-tabs :deep(.el-tabs__active-bar) {
  background: var(--gradient-primary) !important;
  height: 3px !important;
  border-radius: 2px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}
.login-tabs :deep(.el-tabs__content) {
  overflow: visible;
}
</style>
